import { apiPost } from './api'

export interface FoldedChildDto {
  uuid: string
  name: string
  date: string
}

export interface RelatedContractDto {
  uuid: string
  name: string
  date: string
  kind: string
  /** For frameworkAmendment/frameworkSupplement only: the framework head uuid this folds into. */
  via?: string
}

/** The document roles the stored Engagement gives its documents (ADR 0051) → the related-group kinds. */
const KIND_BY_ROLE: Record<string, string> = {
  root: 'primary',
  amendment: 'amendment',
  supplement: 'supplement',
  framework: 'framework',
  frameworkAmendment: 'frameworkAmendment',
  frameworkSupplement: 'frameworkSupplement',
  predecessor: 'superseded',
  cancelled: 'cancelled',
}

interface RawReadResponse {
  objects: Record<string, any>[]
  truncated?: boolean
}

export interface FoldedContracts {
  /** The Engagements of the project, each adapted to the dashboard contract shape (`uuid` is the root document's). */
  objects: Record<string, any>[]
  childrenByRoot: Map<string, FoldedChildDto[]>
  supplementsByRoot: Map<string, FoldedChildDto[]>
  /** Framework documents (ADR 0020) folded INTO each dependent root — their terms are in force there. */
  basedOnByRoot: Map<string, FoldedChildDto[]>
  relatedByRoot: Map<string, RelatedContractDto[]>
  /** True when more Engagements existed than the read returned (a bounded read) — surface to the user. */
  hasMore: boolean
}

export interface FoldQueryOptions {
  /**
   * Bounded top-N: when set, a SINGLE page of up to this many Engagements is fetched (server clamps to
   * 500) and pagination is skipped — for deliberate top-N views. Omit to page through every Engagement
   * so dashboard aggregates cover the whole project, not just the first 500.
   */
  rootLimit?: number
}

const EDGE_REF = {
  fields: ['uuid', 'heading'],
  relations: { contract: { fields: ['uuid', 'nameOfAgreement'] } },
}

/**
 * The object tree the insight dashboards read off each stored Engagement (ADR 0051): the arrangement's
 * scalars in force + its primary counterparty, every document folded into it (with its role), and per
 * clause IN FORCE the type tags, classification justifications (typeAssignments) and validity issues
 * plus the forward edge refs. The clause's owner (the document whose wording is in force) rides on the
 * ACTIVE_CLAUSE edge. `clauseTypeTags` is the `types` edge under a domain key.
 */
const ENGAGEMENT_PROJECT = {
  fields: [
    'uuid', 'rootUuid', 'nameOfAgreement', 'category', 'contractType', 'currencyCode',
    'dateOfAgreement', 'commencementDate', 'duration', 'durationUnit',
    'governingCountry', 'governingRegion', 'location', 'issueFlags', 'createdAt',
    'reportedValue', 'reportedValueCanonical', 'reportedValueCurrency', 'reportedValueLabel', 'reportedValuePeriodEnd',
    // Backend-persisted Risk with provenance (ADR 0023), judged per Engagement — the dashboards render
    // these verbatim, never a client-computed tier. riskScore is deliberately absent (internal explainer);
    // riskReasonPoints rides along for the risk modal's admin-only per-reason attribution.
    'riskTier', 'riskBaselineTier', 'riskReasons', 'riskReasonPoints', 'riskDecidedBy', 'riskJustification', 'riskEvaluatedAt',
  ],
  relations: {
    // The baseline rules that fired at the Engagement's last evaluation (ADR 0023): what lets the
    // dashboards show COUNTS OF REAL FINDINGS instead of dressing a local count as a risk tier.
    riskFindings: { fields: ['key', 'kind', 'label', 'category', 'weight', 'points', 'minTier'] },
    // Every document folded into the Engagement, with its fold role and display name/date on the edge.
    documents: { fields: ['uuid'], edgeFields: ['role', 'via', 'name', 'date'] },
    clauses: {
      relation: 'activeClauses',
      fields: ['uuid', 'heading'],
      edgeFields: ['owner', 'ownerName', 'viaFramework', 'amendedFrom'],
      // Drop contentless clauses (no tags, justifications, validity issues or edges): the dashboards
      // only read clauses through these relations, so a bare shell is pure payload.
      dropEmpty: true,
      relations: {
        clauseTypeTags: { relation: 'types', fields: ['uuid', 'clauseType', 'code'] },
        typeAssignments: {
          fields: ['uuid', 'certainty', 'justification', 'source'],
          relations: {
            type: { fields: ['clauseType', 'code'] },
            // `values` carries the full array for multi-valued details (the table cells show the
            // primary value + a "+N" hover indicator); `value` is its joined display form. Request both.
            details: { fields: ['uuid', 'name', 'value', 'values'] },
          },
        },
        validityIssues: { fields: ['uuid', 'issue', 'explanation', 'severity', 'excerpt', 'recommendation'] },
        amends: EDGE_REF,
        supersedes: EDGE_REF,
        supplements: EDGE_REF,
      },
    },
    primaryCounterparty: { fields: ['uuid', 'name', 'address', 'industry', 'otherNames'] },
  },
}

/** Server `typeAssignments` ({justification, type:{clauseType}, details}) → the dashboards' `clauseJustifications`. */
function adaptClause(raw: Record<string, any>, rootUuid: string): Record<string, any> {
  const { owner, ownerName, ...clause } = raw
  return {
    ...clause,
    // The document whose wording is in force, when it is not the root itself (a folded amendment, a
    // supplement, a framework): what clause deep-links open and what the relationship graph skips.
    ...(owner && owner !== rootUuid ? { ownerContractUuid: owner, ownerContractName: ownerName ?? null } : {}),
    // The server omits empty arrays from the wire payload, so an untagged clause arrives with no
    // clauseTypeTags key; default it (the type declares it required) so dashboard `.some`/`.flatMap`
    // reads never hit undefined.
    clauseTypeTags: raw.clauseTypeTags ?? [],
    clauseJustifications: (raw.typeAssignments ?? []).map((ta: any) => ({
      justification: ta.justification,
      clauseType: { clauseType: ta.type?.clauseType, code: ta.type?.code },
      additionalDetails: (ta.details ?? []).map((d: any) => ({ name: d.name, value: d.value, values: d.values })),
    })),
  }
}

/**
 * One stored Engagement → the contract-shaped field-bag the dashboards consume: identified by its ROOT
 * document's uuid (what links, risk re-evaluation and protection gaps are keyed by), with the
 * Engagement's own uuid kept as `engagementUuid`.
 */
export function adaptEngagement(raw: Record<string, any>): Record<string, any> {
  const rootUuid = String(raw.rootUuid ?? raw.uuid)
  // The documents feed the lineage maps (see lineageMaps), not the dashboard object itself.
  const rest = { ...raw }
  delete rest.documents
  return {
    ...rest,
    uuid: rootUuid,
    engagementUuid: raw.uuid,
    clauses: (raw.clauses ?? []).map((c: Record<string, any>) => adaptClause(c, rootUuid)),
  }
}

/** A folded document ref as the stored `documents` edge carries it. */
function childRef(d: Record<string, any>): FoldedChildDto {
  return { uuid: d.uuid, name: d.name ?? null, date: d.date ?? null }
}

/**
 * The lineage maps the "View Contract(s)" affordances consume, derived from each Engagement's stored
 * `documents` and their roles: amendments (childrenByRoot), supplements, frameworks (basedOnByRoot, the
 * heads plus their own folded lineage, as before) and the whole related group (relatedByRoot).
 */
export function lineageMaps(engagements: Record<string, any>[]): Pick<FoldedContracts, 'childrenByRoot' | 'supplementsByRoot' | 'basedOnByRoot' | 'relatedByRoot'> {
  const childrenByRoot = new Map<string, FoldedChildDto[]>()
  const supplementsByRoot = new Map<string, FoldedChildDto[]>()
  const basedOnByRoot = new Map<string, FoldedChildDto[]>()
  const relatedByRoot = new Map<string, RelatedContractDto[]>()
  for (const e of engagements) {
    const rootUuid = String(e.rootUuid ?? e.uuid)
    const documents: Record<string, any>[] = e.documents ?? []
    const related: RelatedContractDto[] = []
    for (const d of documents) {
      const role = String(d.role ?? '')
      const kind = KIND_BY_ROLE[role]
      if (!kind) continue
      related.push({ ...childRef(d), kind, ...(d.via ? { via: d.via } : {}) })
      if (role === 'amendment') push(childrenByRoot, rootUuid, childRef(d))
      else if (role === 'supplement') push(supplementsByRoot, rootUuid, childRef(d))
      else if (role === 'framework' || role === 'frameworkAmendment' || role === 'frameworkSupplement') push(basedOnByRoot, rootUuid, childRef(d))
    }
    if (related.length > 0) relatedByRoot.set(rootUuid, related)
  }
  return { childrenByRoot, supplementsByRoot, basedOnByRoot, relatedByRoot }
}

function push<T>(map: Map<string, T[]>, key: string, value: T): void {
  const list = map.get(key)
  if (list) list.push(value)
  else map.set(key, [value])
}

/**
 * Fetch a project's arrangements in force — its stored Engagements (ADR 0051): each root document
 * with the amendments, supplements and frameworks folded into it, its clauses in force and the rating
 * judged over them — via the semantic {@code /data/read} endpoint rooted at `engagement`. The lineage
 * maps the "View Contract(s)" affordances consume are derived from each Engagement's documents.
 */
export async function fetchFoldedProjectContracts(
  projectId: string,
  opts: FoldQueryOptions = {},
): Promise<FoldedContracts> {
  const pageSize = Math.min(opts.rootLimit ?? 500, 500)
  // A rootLimit means the caller wants a bounded top-N (single page). With no rootLimit we page through
  // EVERY Engagement, so dashboard counts, value totals and risk rollups cover the whole project.
  const paginate = opts.rootLimit == null

  const raw: Record<string, any>[] = []
  // Pages are disjoint windows over a deterministic order, but dedupe defensively so a boundary
  // re-ordered by a concurrent rebuild can never double-count an Engagement.
  const seen = new Set<string>()

  let offset = 0
  let hasMore = false
  // The server caps a project's Engagements well below this; the bound only stops us spinning.
  const MAX_PAGES = 1000
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await apiPost<RawReadResponse>(`projects/${projectId}/data/read`, {
      entity: 'engagement',
      alias: 'e',
      filters: [],
      // The most valuable arrangements first: the counterparty's reported value in the canonical currency.
      orderBy: [{ column: 'reportedValueCanonical', direction: 'desc' }],
      offset,
      limit: pageSize,
      // Drop full clause text from the list payload — the dashboards only need clause headings plus
      // the type tags/justifications/validity sub-structures; full text is lazy-fetched in the clause
      // detail panel.
      project: ENGAGEMENT_PROJECT,
    })

    for (const e of res.objects ?? []) {
      const key = String(e.rootUuid ?? e.uuid)
      if (seen.has(key)) continue
      seen.add(key)
      raw.push(e)
    }

    hasMore = res.truncated === true
    if (!paginate || !hasMore) break
    offset += pageSize
  }

  // After a full paginate this is false (loop exits on !truncated); it stays true only if we bailed
  // on the page guard or took a bounded page, so callers can tell a bounded/aborted read from a complete one.
  return { objects: raw.map(adaptEngagement), ...lineageMaps(raw), hasMore }
}
